import { useAction, useAtom } from '@reatom/npm-react'
import styled, { useTheme } from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { ArrowRight2Icon, Button } from '@blade/ui-kit'
import { LIMIT_ORDER_STATUS_LABEL_MAP } from '@root/entities/limit-order/ui'
import {
  OrderUiUtil,
  SWAP_ORDER_STATUS_LABEL_MAP,
} from '@root/entities/order/ui'
import { reatomCancelLimitOrder } from '@root/features/trade/limit-orders'
import { useTranslation } from '@root/shared/localization'
import { CircleOutlinedIcon, TrashIcon } from '@root/shared/ui-kit'

import type { OrderModel } from '@root/entities/order/atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  order: OrderModel
} & ClassNameProps

export const OrdersTableStatusCell: React.FC<Props> = ({
  className,
  order,
}) => {
  const theme = useTheme()

  const [status] = useAtom(order.order.status)
  const { cancelOrderAction, cancelStateAtom } = useMemo(
    () => reatomCancelLimitOrder({ orderId: order.order.id }),
    [order],
  )
  const [cancelState] = useAtom(cancelStateAtom)
  const cancelOrder = useAction(cancelOrderAction)
  const colorAtom = useMemo(
    () => OrderUiUtil.statusColor(order, theme),
    [order, theme],
  )
  const [color] = useAtom(colorAtom)
  const { t } = useTranslation()

  const [label] = useAtom((ctx) => {
    return order.type === 'limit'
      ? t(LIMIT_ORDER_STATUS_LABEL_MAP[ctx.spy(order.order.status)])
      : t(SWAP_ORDER_STATUS_LABEL_MAP[ctx.spy(order.order.status)])
  })

  const shouldShowCancel = order.type === 'limit' && status === 'active'

  return (
    <Root className={className}>
      <StatusContainer $color={color}>
        <CircleOutlinedIcon size={10} />
        <div>{label}</div>
      </StatusContainer>
      <Actions>
        {shouldShowCancel ? (
          <DeleteButton
            busy={AsyncStateN.isLoading(cancelState)}
            variant="ghost"
            iconLeft={<TrashIcon />}
            size="xxs"
            onClick={(e) => {
              e.stopPropagation()
              cancelOrder()
            }}
          />
        ) : undefined}
        <ShowButton variant="ghost" iconLeft={<ArrowRight2Icon />} size="xxs" />
      </Actions>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;

  gap: 12px;
  width: 100%;
`

const StatusContainer = styled.div<{ $color: string }>`
  display: grid;
  grid-auto-flow: column;
  gap: 4px;
  justify-content: start;
  align-items: center;

  color: ${({ $color }) => $color};

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

const Actions = styled.div`
  display: grid;
  grid-template-columns: auto auto;
`

const DeleteButton = styled(Button)`
  color: var(--textA-8);
`

const ShowButton = styled(Button)`
  color: var(--textA-8);
  place-self: end;
`
